/** TanStack Query bindings for every endpoint the UI uses. */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { isIncognito } from '@/lib/incognito'

import { api, ApiError } from './client'
import type {
  Daylist,
  TodayDaylist,
  AddTrackResponse,
  AlbumDetail,
  AlbumSearchResponse,
  ArtistSearchResponse,
  BulkPlaylistResult,
  BulkResult,
  CredentialState,
  CatalogServices,
  DownloadJob,
  LibraryAlbum,
  LibraryItem,
  Paginated,
  Playlist,
  PlaylistDetail,
  PlayEvent,
  PlayerStatePayload,
  Provider,
  ProviderInfo,
  SearchResponse,
  SimilarResponse,
  StorageUsage,
  User,
  UserSettings,
} from './types'

export const keys = {
  me: ['me'] as const,
  settings: ['settings'] as const,
  storage: ['storage'] as const,
  providers: ['providers'] as const,
  search: (q: string, provider?: string) => ['search', q, provider ?? 'all'] as const,
  albumSearch: (q: string, provider?: string) => ['album-search', q, provider ?? 'all'] as const,
  album: (provider: string, externalId: string) => ['album', provider, externalId] as const,
  library: (filters: LibraryFilters) => ['library', filters] as const,
  libraryAlbums: (q: string) => ['library-albums', q] as const,
  libraryAlbum: (albumId: string) => ['library-albums', 'detail', albumId] as const,
  jobs: (status?: string) => ['jobs', status ?? 'all'] as const,
  playlists: ['playlists'] as const,
  playlist: (id: number) => ['playlist', id] as const,
  playerState: ['player-state'] as const,
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export function useMe(options?: Partial<UseQueryOptions<User | null>>) {
  return useQuery<User | null>({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me')
      } catch (error) {
        // Not signed in is a normal state, not a query failure.
        if (error instanceof ApiError && error.isUnauthenticated) return null
        throw error
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
    ...options,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<User>('/auth/login', body),
    onSuccess: (user) => {
      queryClient.setQueryData(keys.me, user)
      void queryClient.invalidateQueries()
    },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      email: string
      password: string
      display_name?: string
      invite_code?: string
    }) => api.post<User>('/auth/register', body),
    onSuccess: (user) => queryClient.setQueryData(keys.me, user),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(keys.me, null)
      queryClient.clear()
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      api.post<void>('/auth/password', body),
  })
}

// ---------------------------------------------------------------------------
// Settings and storage
// ---------------------------------------------------------------------------

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<UserSettings>('/me/settings'),
  })
}

export function useUpdateSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<UserSettings>) =>
      api.patch<UserSettings>('/me/settings', patch),
    onSuccess: (settings) => {
      queryClient.setQueryData(keys.settings, settings)
      void queryClient.invalidateQueries({ queryKey: keys.me })
    },
  })
}

export function useStorage() {
  return useQuery({
    queryKey: keys.storage,
    queryFn: () => api.get<StorageUsage>('/me/storage'),
    staleTime: 30_000,
  })
}

export function useProviders() {
  return useQuery({
    queryKey: keys.providers,
    queryFn: () => api.get<ProviderInfo[]>('/providers'),
    staleTime: 5 * 60_000,
  })
}

/**
 * Save this user's own credential for a provider.
 *
 * Write-only: the response carries a masked preview and a validity check, never
 * the value back. Saving also tests it, so a truncated paste is caught here
 * rather than much later inside a failed download.
 */
export function useSaveCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ provider, secret }: { provider: Provider; secret: string }) =>
      api.put<CredentialState>(`/me/credentials/${provider}`, { secret }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.providers }),
  })
}

export function useTestCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (provider: Provider) => api.post<CredentialState>(`/me/credentials/${provider}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.providers }),
  })
}

export function useDeleteCredential() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (provider: Provider) => api.delete<void>(`/me/credentials/${provider}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.providers }),
  })
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export function useSearch(query: string, provider?: Provider | 'all') {
  const trimmed = query.trim()
  return useQuery({
    queryKey: keys.search(trimmed, provider),
    queryFn: ({ signal }) =>
      api.get<SearchResponse>(
        '/search',
        { q: trimmed, provider: provider && provider !== 'all' ? provider : undefined, limit: 30 },
        signal,
      ),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
    // Keeps the previous list on screen while a new query loads, instead of
    // flashing an empty state on every keystroke.
    placeholderData: (previous) => previous,
  })
}

// ---------------------------------------------------------------------------
// Library
// ---------------------------------------------------------------------------

export interface LibraryFilters {
  q?: string
  favorite?: boolean
  sort?: 'recent' | 'played' | 'title' | 'artist'
  status?: string
}

export function useLibrary(filters: LibraryFilters = {}) {
  return useInfiniteQuery({
    queryKey: keys.library(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      if (pageParam) {
        // `next` is an absolute URL from DRF; strip the prefix the client adds.
        const path = new URL(pageParam).pathname.replace('/api/v1', '')
        const search = new URL(pageParam).search
        return api.get<Paginated<LibraryItem>>(`${path}${search}`, undefined, signal)
      }
      return api.get<Paginated<LibraryItem>>(
        '/library/tracks',
        {
          q: filters.q,
          favorite: filters.favorite ? 'true' : undefined,
          sort: filters.sort,
          status: filters.status,
        },
        signal,
      )
    },
    getNextPageParam: (last) => last.next,
  })
}

/**
 * A randomised queue drawn from the whole library, for "just start listening".
 *
 * A mutation rather than a query on purpose: it is an action with a fresh result
 * each time, and caching a shuffle would hand back the same "random" order.
 * Shuffling happens server-side because the library list is paginated — picking
 * from what the browser has loaded would only ever shuffle the first page.
 */
export function useShuffleLibrary() {
  return useMutation({
    mutationFn: (filters: { q?: string; favorite?: boolean; album?: number | 'singles' } = {}) =>
      api.get<{ results: LibraryItem[]; count: number }>('/library/tracks/shuffle', {
        q: filters.q || undefined,
        favorite: filters.favorite ? 'true' : undefined,
        album: filters.album !== undefined ? String(filters.album) : undefined,
      }),
  })
}

/** Playback is temporary; saving is an explicit, separate intent. */
export function useRequestTrack(defaultIntent: 'play' | 'save' = 'play') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ intent = defaultIntent, ...body }: {
      provider: Provider; external_id: string; intent?: 'play' | 'save'
    }) => api.post<AddTrackResponse>(
      intent === 'save' ? '/library/tracks' : '/playback/tracks',
      { ...body, incognito: intent === 'play' && isIncognito() },
    ),
    onSuccess: () => invalidateLibrary(queryClient),
  })
}

/** Explicitly save a song, including one already in the playback cache. */
export function useAddTrack() {
  return useRequestTrack('save')
}

/** Toggle permanent membership without deleting the current playback copy. */
export function useSetLibrarySaved() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, saved }: { id: number; saved: boolean }) => saved
      ? api.post<LibraryItem>(`/library/tracks/${id}/save`)
      : api.delete<LibraryItem>(`/library/tracks/${id}/save`),
    onSuccess: (item) => {
      queryClient.setQueryData(['library', 'item', item.id], item)
      invalidateLibrary(queryClient)
      void queryClient.invalidateQueries({ queryKey: keys.playlists })
      void queryClient.invalidateQueries({ queryKey: ['playlist'] })
    },
  })
}

export function useToggleFavorite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, favorite }: { id: number; favorite: boolean }) =>
      favorite
        ? api.post<LibraryItem>(`/library/tracks/${id}/favorite`)
        : api.delete<LibraryItem>(`/library/tracks/${id}/favorite`),
    onSuccess: () => invalidateLibrary(queryClient),
  })
}

export function useRemoveFromLibrary() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, deleteFile = true }: { id: number; deleteFile?: boolean }) =>
      api.delete<void>(`/library/tracks/${id}`, { delete_file: deleteFile ? 'true' : 'false' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['library'] })
      void queryClient.invalidateQueries({ queryKey: keys.storage })
      void queryClient.invalidateQueries({ queryKey: ['artists'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Albums
// ---------------------------------------------------------------------------

/** Artist search uses provider artist indexes independently of song results. */
export function useArtistSearch(query: string, provider?: Provider | 'all') {
  const trimmed = query.trim()
  return useQuery({
    queryKey: ['artist-search', trimmed, provider ?? 'all'],
    queryFn: ({ signal }) => api.get<ArtistSearchResponse>(
      '/search/artists',
      { q: trimmed, provider: provider && provider !== 'all' ? provider : undefined, limit: 30 },
      signal,
    ),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
  })
}

/** Album search. Providers without albums are skipped, not reported as errors. */
export function useAlbumSearch(query: string, provider?: Provider | 'all') {
  const trimmed = query.trim()
  return useQuery({
    queryKey: keys.albumSearch(trimmed, provider),
    queryFn: ({ signal }) =>
      api.get<AlbumSearchResponse>(
        '/search/albums',
        { q: trimmed, provider: provider && provider !== 'all' ? provider : undefined, limit: 30 },
        signal,
      ),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  })
}

/** An album with its full track listing, annotated with what this user already has. */
export function useAlbum(provider: Provider | undefined, externalId: string | undefined) {
  return useQuery({
    queryKey: keys.album(provider ?? '', externalId ?? ''),
    queryFn: () => api.get<AlbumDetail>(`/albums/${provider}/${externalId}`),
    enabled: Boolean(provider && externalId),
  })
}

/** Queue a whole album. Defaults to filling in only what is missing. */
export function useDownloadAlbum() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { provider: Provider; external_id: string; missing_only?: boolean }) =>
      api.post<BulkResult>('/library/albums', body),
    onSuccess: () => invalidateLibrary(queryClient),
  })
}

export function useLibraryAlbums(search?: string) {
  return useQuery({
    queryKey: keys.libraryAlbums(search ?? ''),
    queryFn: ({ signal }) =>
      api.get<{ results: LibraryAlbum[] }>('/library/albums', { q: search || undefined }, signal),
  })
}

/** Tracks the user owns from one album. Pass "singles" for the albumless bucket. */
export function useLibraryAlbumTracks(albumId: number | 'singles' | undefined) {
  return useQuery({
    queryKey: keys.libraryAlbum(String(albumId ?? '')),
    queryFn: () => api.get<{ results: LibraryItem[] }>(`/library/albums/${albumId}`),
    enabled: albumId !== undefined,
  })
}

// ---------------------------------------------------------------------------
// Bulk operations
// ---------------------------------------------------------------------------

/** Queue a multi-selection. Each track still becomes its own job. */
export function useBulkAddTracks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tracks: Array<{ provider: Provider; external_id: string }>) =>
      api.post<BulkResult>('/library/tracks/bulk', { tracks }),
    onSuccess: () => invalidateLibrary(queryClient),
  })
}

export function useBulkFavorite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, favorite }: { ids: number[]; favorite: boolean }) =>
      api.post<{ updated: number }>('/library/tracks/bulk/favorite', { ids, favorite }),
    onSuccess: () => invalidateLibrary(queryClient),
  })
}

export function useBulkRemove() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, deleteFile = true }: { ids: number[]; deleteFile?: boolean }) =>
      api.post<{ removed: number }>('/library/tracks/bulk/remove', {
        ids,
        delete_file: deleteFile,
      }),
    onSuccess: () => invalidateLibrary(queryClient),
  })
}

/** Playlist additions also add songs to the permanent library. */
export function useBulkAddToPlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      playlistId,
      ...body
    }: {
      playlistId: number
      track_ids?: number[]
      tracks?: Array<{ provider: Provider; external_id: string }>
    }) => api.post<BulkPlaylistResult>(`/playlists/${playlistId}/tracks/bulk`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: keys.playlist(variables.playlistId) })
      void queryClient.invalidateQueries({ queryKey: keys.playlists })
      invalidateLibrary(queryClient)
    },
  })
}

/** Everything a library change can invalidate, in one place. */
function invalidateLibrary(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['daylists'] })
  void queryClient.invalidateQueries({ queryKey: ['library'] })
  void queryClient.invalidateQueries({ queryKey: ['library-albums'] })
  void queryClient.invalidateQueries({ queryKey: ['jobs'] })
  void queryClient.invalidateQueries({ queryKey: ['search'] })
  void queryClient.invalidateQueries({ queryKey: ['artists'] })
  void queryClient.invalidateQueries({ queryKey: ['similar'] })
  void queryClient.invalidateQueries({ queryKey: ['album'] })
  void queryClient.invalidateQueries({ queryKey: keys.storage })
}

// ---------------------------------------------------------------------------
// Download jobs
// ---------------------------------------------------------------------------

export function useJobs(status: 'active' | 'all' = 'active') {
  return useQuery({
    queryKey: keys.jobs(status),
    queryFn: () =>
      api.get<Paginated<DownloadJob>>('/jobs', {
        status: status === 'active' ? 'active' : undefined,
      }),
    // The SSE stream drives updates; this is the fallback for a dropped stream.
    refetchInterval: (query) => {
      const jobs = query.state.data?.results ?? []
      return jobs.some((job) => job.status === 'queued' || job.status === 'running')
        ? 5_000
        : false
    },
  })
}

export function useRetryJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<DownloadJob>(`/jobs/${id}/retry`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] })
      void queryClient.invalidateQueries({ queryKey: ['library'] })
    },
  })
}

/**
 * Cancel a running download, or drop a finished one from the history list.
 *
 * One endpoint, because that is what the X on a row means either way. Removing a
 * finished row deletes the job record only — the downloaded track stays put.
 */
export function useRetryAllJobs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ requeued: number; skipped: number }>('/jobs/retry-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] })
      invalidateLibrary(queryClient)
    },
  })
}

export function useCancelJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/jobs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

/** Empty the finished-downloads list. Never cancels anything still in flight. */
export function useClearJobHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (only?: 'failed') =>
      api.delete<{ deleted: number }>('/jobs/history', only ? { status: only } : undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

// ---------------------------------------------------------------------------
// Playlists
// ---------------------------------------------------------------------------

export function usePlaylists() {
  return useQuery({
    queryKey: keys.playlists,
    queryFn: () => api.get<Playlist[]>('/playlists'),
  })
}

export function usePlaylist(id: number | undefined) {
  return useQuery({
    queryKey: keys.playlist(id ?? 0),
    queryFn: () => api.get<PlaylistDetail>(`/playlists/${id}`),
    enabled: typeof id === 'number' && Number.isFinite(id),
  })
}

export function useCreatePlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      api.post<Playlist>('/playlists', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.playlists }),
  })
}

export interface ImportPlaylistResult {
  playlist: Playlist
  imported: number
  skipped: number
  source_total: number
  unavailable_here: number
  source_title: string
  source_owner: string
  provider: string
}

/** Which services can be imported from — drives the dialog's hint text. */
export interface IncognitoHeld {
  count: number
  bytes: number
  ttl_hours: number
}

/** What the current private session is holding on the server. */
export function useIncognitoHeld(enabled: boolean) {
  return useQuery({
    queryKey: ['incognito-held'],
    queryFn: () => api.get<IncognitoHeld>('/library/tracks/incognito'),
    enabled,
    staleTime: 15 * 1000,
  })
}

export function useDiscardIncognito() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<{ discarded: number }>('/library/tracks/incognito'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['incognito-held'] })
      invalidateLibrary(queryClient)
    },
  })
}

/**
 * A bounded history snapshot for Settings and Home. Home asks for more entries
 * so repeat listens can be collapsed into distinct shortcuts and artist seeds.
 * `useHistoryPages` is the full, paginated browser.
 */
export function useRecentHistory(limit = 5) {
  return useQuery({
    queryKey: ['history', 'recent', limit],
    queryFn: ({ signal }) =>
      api.get<Paginated<PlayEvent>>('/me/history', { page_size: String(limit) }, signal),
    staleTime: 30 * 1000,
  })
}

/** The whole history, a page at a time, for the full list. */
export function useHistoryPages() {
  return useInfiniteQuery({
    queryKey: ['history', 'all'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      if (pageParam) {
        // `next` is an absolute URL from DRF; strip the prefix the client adds.
        const url = new URL(pageParam)
        return api.get<Paginated<PlayEvent>>(
          `${url.pathname.replace('/api/v1', '')}${url.search}`,
          undefined,
          signal,
        )
      }
      return api.get<Paginated<PlayEvent>>('/me/history', { page_size: '50' }, signal)
    },
    getNextPageParam: (last) => last.next,
    staleTime: 30 * 1000,
  })
}

export function useClearHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<{ deleted: number }>('/me/history'),
    onSuccess: () => {
      // Prefix key: clears the card's preview and the full list together.
      void queryClient.invalidateQueries({ queryKey: ['history'] })
      void queryClient.invalidateQueries({ queryKey: ['daylists'] })
    },
  })
}

export function useTodayDaylist() {
  return useQuery({
    queryKey: ['daylists', 'today'],
    queryFn: ({ signal }) => api.get<TodayDaylist>('/daylists/today', undefined, signal),
    refetchInterval: (query) => ['queued', 'generating'].includes(query.state.data?.status ?? '') ? 3000 : 60000,
    refetchOnWindowFocus: true,
  })
}

export function useDaylist(id: number | undefined) {
  return useQuery({
    queryKey: ['daylists', id],
    queryFn: ({ signal }) => api.get<Daylist>(`/daylists/${id}`, undefined, signal),
    enabled: id !== undefined,
    refetchInterval: (query) => ['queued', 'generating'].includes(query.state.data?.status ?? '') ? 3000 : false,
  })
}

export function useKeepDaylist() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<{ playlist_id: number }>(`/daylists/${id}/playlist`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['daylists'] })
      void client.invalidateQueries({ queryKey: keys.playlists })
      invalidateLibrary(client)
    },
  })
}

/**
 * What else is like this.
 *
 * `refetchOnWindowFocus` is off deliberately. The upstream radio returns a
 * different set every call, so a refetch on focus would silently reshuffle the
 * screen the listener was reading. Re-rolling is a thing you ask for, not
 * something that happens while you look away.
 */
export function useSimilar(seed: { localArtistId?: string; artistId?: number; provider?: string; artist?: string } | null) {
  const enabled = Boolean(seed?.localArtistId || seed?.artistId || seed?.artist)
  return useQuery({
    queryKey: seed?.localArtistId ? ['similar', 'local', seed.localArtistId] : ['similar', seed?.artistId ?? null, seed?.provider ?? null, seed?.artist ?? null],
    queryFn: ({ signal }) =>
      api.get<SimilarResponse>(
        seed?.localArtistId ? `/discover/local/${encodeURIComponent(seed.localArtistId)}` : '/discover/similar',
        seed?.localArtistId ? {} : seed?.artistId
          ? { artist_id: String(seed.artistId) }
          : { artist: seed?.artist, provider: seed?.provider },
        signal,
      ),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useImportSources() {
  return useQuery({
    queryKey: ['playlist-import-sources'],
    queryFn: () => api.get<{ providers: { slug: string; name: string }[] }>('/playlists/import'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useImportPlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { url: string; name?: string }) =>
      api.post<ImportPlaylistResult>('/playlists/import', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.playlists })
      invalidateLibrary(queryClient)
    },
  })
}

export function useUpdatePlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; description?: string }) =>
      api.patch<Playlist>(`/playlists/${id}`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: keys.playlists })
      void queryClient.invalidateQueries({ queryKey: keys.playlist(variables.id) })
    },
  })
}

export function useDeletePlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/playlists/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.playlists }),
  })
}

export function useAddToPlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      playlistId,
      ...body
    }: {
      playlistId: number
      track_id?: number
      provider?: Provider
      external_id?: string
    }) => api.post(`/playlists/${playlistId}/tracks`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: keys.playlist(variables.playlistId) })
      void queryClient.invalidateQueries({ queryKey: keys.playlists })
      invalidateLibrary(queryClient)
    },
  })
}

export function useRemoveFromPlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ playlistId, entryId }: { playlistId: number; entryId: number }) =>
      api.delete<void>(`/playlists/${playlistId}/tracks/${entryId}`),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: keys.playlist(variables.playlistId) })
      void queryClient.invalidateQueries({ queryKey: keys.playlists })
    },
  })
}

export function useReorderPlaylist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      playlistId,
      entryId,
      toPosition,
    }: {
      playlistId: number
      entryId: number
      toPosition: number
    }) =>
      api.post<void>(`/playlists/${playlistId}/reorder`, {
        entry_id: entryId,
        to_position: toPosition,
      }),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({ queryKey: keys.playlist(variables.playlistId) }),
  })
}

// ---------------------------------------------------------------------------
// Player state
// ---------------------------------------------------------------------------

export function usePlayerState() {
  return useQuery({
    queryKey: keys.playerState,
    queryFn: () => api.get<PlayerStatePayload>('/player/state'),
    staleTime: Infinity,
  })
}

export function useCatalogServices() {
  return useQuery({
    queryKey: ['catalog-services'],
    queryFn: () => api.get<CatalogServices>('/catalog/services'),
    staleTime: 60_000,
  })
}
